import { useEffect, useState } from 'react';
import { Card, Form, Input, Button, message, Typography, Space, Upload } from 'antd';
import { UserOutlined, BankOutlined, PhoneOutlined, EnvironmentOutlined, UploadOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import PlacesAutocomplete from '../components/PlacesAutocomplete';
import type { UploadProps } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';

const { Title } = Typography;

interface CompanyProfile {
  bedrijfsnaam: string;
  kvk_nummer: string;
  btw_nummer: string;
  adres: string;
  postcode: string;
  stad: string;
  land: string;
  logo_url?: string;
  voornaam_financieel?: string;
  achternaam_financieel?: string;
  telefoon_financieel?: string;
  email_financieel?: string;
}

interface UserProfile {
  email: string;
  voornaam: string;
  achternaam: string;
  telefoon: string;
}

interface ProfileFormData extends CompanyProfile, UserProfile {}

export default function Profile() {
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string>();
  const { user } = useAuth();
  const [form] = Form.useForm();

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      // Get company data from companies table
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (companyError && companyError.code !== 'PGRST116') {
        throw companyError;
      }

      // Get user metadata
      const userData = user?.user_metadata || {};
      
      // Combine data and set form values
      form.setFieldsValue({
        // Company data
        ...(companyData || {}),
        // User data from metadata
        email: user?.email,
        voornaam: userData.voornaam || '',
        achternaam: userData.achternaam || '',
        telefoon: userData.telefoon || '',
      });
      
      if (companyData?.logo_url) {
        setLogoUrl(companyData.logo_url);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      message.error('Er is een fout opgetreden bij het laden van uw profiel.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: ProfileFormData) => {
    try {
      setLoading(true);

      // Separate user data from company data
      const { 
        email, 
        voornaam, 
        achternaam, 
        telefoon,
        ...companyData 
      } = values;

      // Update user metadata
      const { error: userError } = await supabase.auth.updateUser({
        data: {
          voornaam,
          achternaam,
          telefoon
        }
      });

      if (userError) throw userError;

      // Update company data (including financial contact info)
      const { error: companyError } = await supabase
        .from('companies')
        .update({
          ...companyData,
          logo_url: logoUrl,
        })
        .eq('user_id', user?.id);

      if (companyError) throw companyError;

      message.success('Profiel succesvol bijgewerkt!');
    } catch (error) {
      console.error('Error updating profile:', error);
      message.error('Er is een fout opgetreden bij het bijwerken van uw profiel.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (file: RcFile) => {
    try {
      setLoading(true);

      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        message.error('Logo mag niet groter zijn dan 2MB');
        return false;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        message.error('Alleen afbeeldingsbestanden zijn toegestaan');
        return false;
      }

      // Delete old logo if exists
      if (logoUrl) {
        const oldFileName = logoUrl.split('/').pop();
        if (oldFileName) {
          await supabase.storage
            .from('company-logos')
            .remove([oldFileName]);
        }
      }

      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}-${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('company-logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('company-logos')
        .getPublicUrl(fileName);

      // Update state and save to profile
      setLogoUrl(publicUrl);
      const { error: updateError } = await supabase
        .from('companies')
        .update({ logo_url: publicUrl })
        .eq('user_id', user?.id);

      if (updateError) {
        console.error('Update error:', updateError);
        throw updateError;
      }

      message.success('Logo succesvol geüpload');
      return false; // Prevent default upload behavior
    } catch (error) {
      console.error('Error uploading logo:', error);
      message.error('Er is een fout opgetreden bij het uploaden van het logo');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const uploadProps: UploadProps = {
    name: 'logo',
    showUploadList: false,
    beforeUpload: handleLogoUpload,
    accept: 'image/*',
  };

  const handlePlaceSelect = (address: { street: string, postalCode: string, city: string }) => {
    form.setFieldsValue({
      adres: address.street,
      postcode: address.postalCode,
      stad: address.city,
    });
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card
        style={{
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
        }}
      >
        <div style={{ 
          maxWidth: 800, 
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px'
        }}>
          <Title level={2} style={{ marginBottom: 0 }}>Bedrijfsprofiel</Title>
          
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
          >
            <div>
              <Title level={4} style={{ marginBottom: '16px' }}>Persoonlijke gegevens</Title>
              
              <Space style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                <Form.Item
                  name="voornaam"
                  rules={[{ required: true, message: 'Voer uw voornaam in' }]}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Voornaam"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="achternaam"
                  rules={[{ required: true, message: 'Voer uw achternaam in' }]}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Achternaam"
                    size="large"
                  />
                </Form.Item>
              </Space>

              <Form.Item
                name="email"
                rules={[
                  { required: true, message: 'Voer uw e-mailadres in' },
                  { type: 'email', message: 'Voer een geldig e-mailadres in' }
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="E-mailadres"
                  disabled
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="telefoon"
                rules={[{ required: true, message: 'Voer uw telefoonnummer in' }]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="Telefoonnummer"
                  size="large"
                />
              </Form.Item>
            </div>

            <div>
              <Title level={4} style={{ marginBottom: '16px' }}>Bedrijfsgegevens</Title>
              
              {/* Logo Upload */}
              <Form.Item label="Bedrijfslogo" style={{ marginBottom: '24px' }}>
                <Space align="start" size="middle">
                  {logoUrl && (
                    <img
                      src={logoUrl}
                      alt="Bedrijfslogo"
                      style={{
                        width: 100,
                        height: 100,
                        objectFit: 'contain',
                        border: '1px solid #d9d9d9',
                        borderRadius: 4,
                        padding: 4,
                        backgroundColor: '#fff'
                      }}
                    />
                  )}
                  <Upload {...uploadProps}>
                    <Button 
                      icon={<UploadOutlined />} 
                      loading={loading}
                    >
                      {logoUrl ? 'Logo wijzigen' : 'Logo uploaden'}
                    </Button>
                  </Upload>
                </Space>
              </Form.Item>

              <Form.Item
                name="bedrijfsnaam"
                rules={[{ required: true, message: 'Voer uw bedrijfsnaam in' }]}
              >
                <Input
                  prefix={<BankOutlined />}
                  placeholder="Bedrijfsnaam"
                  size="large"
                />
              </Form.Item>

              <Space style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                <Form.Item
                  name="kvk_nummer"
                  rules={[{ required: true, message: 'Voer uw KVK nummer in' }]}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <Input placeholder="KVK Nummer" size="large" />
                </Form.Item>

                <Form.Item
                  name="btw_nummer"
                  rules={[{ required: true, message: 'Voer uw BTW nummer in' }]}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <Input placeholder="BTW Nummer" size="large" />
                </Form.Item>
              </Space>
            </div>

            <div>
              <Title level={4} style={{ marginBottom: '16px' }}>Financiële contactgegevens</Title>
              
              <Space style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                <Form.Item
                  name="voornaam_financieel"
                  rules={[{ required: true, message: 'Voer de voornaam van uw financieel contactpersoon in' }]}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Voornaam"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="achternaam_financieel"
                  rules={[{ required: true, message: 'Voer de achternaam van uw financieel contactpersoon in' }]}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Achternaam"
                    size="large"
                  />
                </Form.Item>
              </Space>

              <Form.Item
                name="telefoon_financieel"
                rules={[{ required: true, message: 'Voer het telefoonnummer van uw financieel contactpersoon in' }]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="Telefoonnummer"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="email_financieel"
                rules={[
                  { required: true, message: 'Voer het e-mailadres van uw financieel contactpersoon in' },
                  { type: 'email', message: 'Voer een geldig e-mailadres in' }
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="E-mailadres"
                  size="large"
                />
              </Form.Item>
            </div>

            <div>
              <Title level={4} style={{ marginBottom: '16px' }}>Adresgegevens</Title>
              
              <Form.Item style={{ marginBottom: '24px' }}>
                <PlacesAutocomplete
                  onSelect={handlePlaceSelect}
                />
              </Form.Item>

              <Form.Item
                name="adres"
                rules={[{ required: true, message: 'Voer uw adres in' }]}
              >
                <Input
                  prefix={<EnvironmentOutlined />}
                  placeholder="Adres"
                  size="large"
                />
              </Form.Item>

              <Space style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                <Form.Item
                  name="postcode"
                  rules={[{ required: true, message: 'Voer uw postcode in' }]}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <Input placeholder="Postcode" size="large" />
                </Form.Item>

                <Form.Item
                  name="stad"
                  rules={[{ required: true, message: 'Voer uw stad in' }]}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <Input placeholder="Stad" size="large" />
                </Form.Item>
              </Space>
            </div>

            <Form.Item style={{ marginBottom: 0, marginTop: '16px' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                style={{ minWidth: '160px' }}
              >
                Wijzigingen opslaan
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Card>
    </div>
  );
}
